import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FaBoxes, FaEye, FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import productService from '../../utils/productService';

const ProductList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setProducts(productService.getAll());
  }, []);

  const refresh = () => setProducts(productService.getAll());

  const handleView = (product) => {
    navigate(`/dashboard/products/${product.id}`);
  };

  const handleEdit = (product) => {
    navigate(`/dashboard/products/${product.id}/edit`);
  };

  const handleAddStock = (product) => {
    navigate(`/dashboard/products/${product.id}/stock`);
  };

  const handleAdd = () => {
    navigate('/dashboard/products/new');
  };

  const handleDelete = (product) => {
    if (window.confirm(`Delete ${product.name}?`)) {
      productService.remove(product.id);
      refresh();
    }
  };

  const totalStock = products.reduce((sum, product) => sum + (Number(product.stock) || 0), 0);

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
            {t('dashboard.products.listLabel')}
          </p>
          <p className="mt-2 text-sm text-slate-600">
            {t('dashboard.products.summary', { count: totalStock })}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00595A] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004446]"
        >
          <FaPlus className="h-4 w-4" />
          {t('dashboard.products.addButton')}
        </button>
      </div>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {t('dashboard.products.name')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {t('dashboard.products.stock')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {t('dashboard.products.required')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {t('dashboard.products.waiting')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {t('dashboard.products.price')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {t('dashboard.products.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {products.map((product) => (
              <tr key={product.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                  {product.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {product.stock}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {product.totalRequired}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {product.customersWaiting}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  ${Number(product.price).toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleView(product)}
                      className="inline-flex items-center justify-center rounded-md bg-slate-100 p-2 text-slate-700 transition hover:bg-slate-200"
                      aria-label={t('dashboard.common.view')}
                      title={t('dashboard.common.view')}
                    >
                      <FaEye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(product)}
                      className="inline-flex items-center justify-center rounded-md bg-amber-100 p-2 text-amber-800 transition hover:bg-amber-200"
                      aria-label={t('dashboard.common.edit')}
                      title={t('dashboard.common.edit')}
                    >
                      <FaPencilAlt className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleAddStock(product)}
                      className="inline-flex items-center justify-center rounded-md bg-emerald-100 p-2 text-emerald-800 transition hover:bg-emerald-200"
                      aria-label={t('dashboard.products.addStock')}
                      title={t('dashboard.products.addStock')}
                    >
                      <FaBoxes className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product)}
                      className="inline-flex items-center justify-center rounded-md bg-rose-100 p-2 text-rose-800 transition hover:bg-rose-200"
                      aria-label={t('dashboard.common.delete')}
                      title={t('dashboard.common.delete')}
                    >
                      <FaTrash className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-3 md:hidden">
        {products.map((product) => (
          <article
            key={product.id}
            className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">{product.name}</h3>
                <p className="mt-1 text-sm text-slate-500">${Number(product.price).toFixed(2)}</p>
              </div>
              <span className="rounded-md bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                {t('dashboard.products.stock')}: {product.stock}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{t('dashboard.products.required')}</p>
                <p className="mt-1 font-semibold text-slate-800">{product.totalRequired}</p>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{t('dashboard.products.waiting')}</p>
                <p className="mt-1 font-semibold text-slate-800">{product.customersWaiting}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => handleView(product)}
                className="inline-flex flex-1 items-center justify-center rounded-md bg-slate-100 px-3 py-3 text-slate-700 transition hover:bg-slate-200"
                aria-label={t('dashboard.common.view')}
                title={t('dashboard.common.view')}
              >
                <FaEye className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleEdit(product)}
                className="inline-flex flex-1 items-center justify-center rounded-md bg-amber-100 px-3 py-3 text-amber-800 transition hover:bg-amber-200"
                aria-label={t('dashboard.common.edit')}
                title={t('dashboard.common.edit')}
              >
                <FaPencilAlt className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleAddStock(product)}
                className="inline-flex flex-1 items-center justify-center rounded-md bg-emerald-100 px-3 py-3 text-emerald-800 transition hover:bg-emerald-200"
                aria-label={t('dashboard.products.addStock')}
                title={t('dashboard.products.addStock')}
              >
                <FaBoxes className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(product)}
                className="inline-flex flex-1 items-center justify-center rounded-md bg-rose-100 px-3 py-3 text-rose-800 transition hover:bg-rose-200"
                aria-label={t('dashboard.common.delete')}
                title={t('dashboard.common.delete')}
              >
                <FaTrash className="h-4 w-4" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default ProductList;
